import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout";
import CommonLoadingSpinner from "../../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import styles from "./BeneficiaryDetails.module.scss";
import { useGetBeneficiaryByIdQuery, useDeleteBeneficiaryMutation } from "../../../../service/beneficiary-service";
import { ConsumerType } from "../../../../model/energy-profile";
import { formatDate, formatTime } from "../../../../utils";
import Button from "../../../../common/ekedc-components/button/Button";
import { getErrorMessage } from "../../../../utils/error-handler";
import { ConfirmationModal } from "../../components";

const typeLabel = (t: ConsumerType) =>
	t === ConsumerType.Prepaid ? "Prepaid" : t === ConsumerType.Postpaid ? "Postpaid" : "Unknown";

const getInitials = (name: string): string => {
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
	return name.slice(0, 2).toUpperCase();
};

export const BeneficiaryDetailsPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useGetBeneficiaryByIdQuery(id!);
	const [deleteBeneficiary, { isLoading: isDeleting }] = useDeleteBeneficiaryMutation();
	const [showConfirm, setShowConfirm] = useState(false);

	const handleDelete = async () => {
		try {
			await deleteBeneficiary(id!).unwrap();
			toast.success("Beneficiary removed successfully");
			navigate(-1);
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) ?? "Failed to remove beneficiary");
		}
	};

	return (
		<CustomerPageLayout title="Beneficiary Details">
			<div className={styles.pageContainer}>
				<CommonLoadingSpinner loading={isLoading}>
					<>
						{isError && (
							<div className={styles.statusBlock}>
								<span>Couldn't load beneficiary details.</span>
								<button type="button" className={styles.retryBtn} onClick={() => refetch()}>
									Retry
								</button>
							</div>
						)}

						{!isError && data && (
							<div className={styles.contentContainer}>
								
							<div className={styles.card}>
								<div className={styles.initialsHeader}>
									<div className={styles.initContainer}>
										{getInitials(data.customerName || data.identifier)}
									</div>
								</div>
								<div className={styles.cardBody}>

									<div className={styles.row}>
										<span className={styles.label}>Customer Name</span>
										<span className={styles.value}>{data.customerName || "—"}</span>
									</div>
									<div className={styles.row}>
										<span className={styles.label}>Meter Number</span>
										<span className={styles.value}>{data.identifier}</span>
									</div>
									{data.address && (
										<div className={styles.row}>
											<span className={styles.label}>Service Address</span>
											<span className={styles.value}>{data.address}</span>
										</div>
									)}

									<div className={styles.row}>
										<span className={styles.label}>Service Type</span>
										<span className={styles.value}>{typeLabel(data.type)}</span>
									</div>

									<div className={styles.row}>
										<span className={styles.label}>Distribution Company</span>
										<span className={styles.value}>Eko Electricity Distribution PLC</span>
									</div>

									<div className={styles.row}>
										<span className={styles.label}>Data Added</span>
										<span className={styles.value}>{formatDate(data.createdAt)}, {formatTime(data.createdAt).toLocaleLowerCase()}</span>
									</div>

								</div>
								{ data.lastPurchase && (
									<div className={styles.lastPurchase}>
										<span className={styles.label}>Last Purchase</span>
										<span className={styles.value}>{formatDate(data.lastPurchase)}, {formatTime(data.lastPurchase).toLocaleLowerCase()}</span>
									</div>
								)}
							</div>
							<Button text="Remove Beneficiary" variant="secondary" onClick={() => setShowConfirm(true)} loading={isDeleting} loadingText="Removing Beneficiary..."/>
							</div>
						)}
					</>
				</CommonLoadingSpinner>
			</div>

			<ConfirmationModal
				isOpen={showConfirm}
				onClose={() => setShowConfirm(false)}
				onConfirm={handleDelete}
				title="Remove Beneficiary"
				message={`Are you sure you want to remove ${data?.identifier ?? "this meter"} from your saved beneficiaries?`}
				confirmText="Remove"
				cancelText="Cancel"
				isLoading={isDeleting}
				loadingText="Removing..."
			/>
		</CustomerPageLayout>
	);
};
